import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { apiGet, apiPatch, AuthError } from '../api';
import { useClient, PLATFORM_LABEL } from '../ClientContext';
import Icon from '../components/Icon';
import PlatformSwitch from '../components/PlatformSwitch';

/**
 * Rules Config.
 *
 * Three changes, all of them about making a threshold mean something.
 *
 *   Plain English.   Every rule now says what it does in a sentence, with the
 *                    current value in it. `min_audience_size_flag` told you
 *                    nothing about what happens when it trips.
 *
 *   Typed editors.   Money was edited as raw pence — you typed 50000 to mean
 *                    £500, while the row above displayed £500. That is a
 *                    footgun on a page that decides how much money an agent
 *                    may commit. Money fields are pounds now, and the
 *                    conversion happens here.
 *
 *   Impact.          A ceiling is meaningless until you know how much of your
 *                    own traffic sits either side of it. Numeric rules show
 *                    what the current value would catch, recalculated as you
 *                    type; the rest show how often they have actually fired.
 *
 * Decisions record rule_triggered as "<category>.<rule_name>", while the rules
 * endpoint returns those two separately — hence the joined key below. Getting
 * that wrong shows every rule as never having fired, which looks like working
 * software.
 */

const CATEGORIES = [
  ['spend_controls',     'Spend controls'],
  ['audience_rules',     'Audience rules'],
  ['campaign_structure', 'Campaign structure'],
  ['creative_rules',     'Creative rules'],
];

const money = (n) => `£${(n || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
const num = (n) => Number(n || 0).toLocaleString('en-GB');

/**
 * What each rule is, in terms a person can act on.
 *
 * `kind` drives the editor. Anything not listed falls back to a text field and
 * its raw name, so a rule added on the server appears here rather than
 * vanishing.
 */
const META = {
  max_daily_budget_pence: {
    label: 'Daily budget ceiling', kind: 'money', unit: 'per campaign, per day',
    says: (v) => <>Blocks any campaign the agent proposes with a daily budget above <b>{money(v / 100)}</b>.</>,
  },
  review_threshold_pence: {
    label: 'Review threshold', kind: 'money', unit: 'per campaign, per day',
    says: (v) => <>Holds anything above <b>{money(v / 100)}</b> a day for a human to look at before it is built.</>,
  },
  max_account_daily_pence: {
    label: 'Account daily ceiling', kind: 'money', unit: 'across every campaign',
    says: (v) => <>Blocks any build that would take total live spend above <b>{money(v / 100)}</b> a day.</>,
  },
  max_creation_budget_pence: {
    label: 'Max creation budget', kind: 'money', unit: 'at creation',
    says: (v) => <>Blocks the creation of any campaign starting above <b>{money(v / 100)}</b> a day.</>,
  },
  min_audience_size: {
    label: 'Minimum audience size', kind: 'number', unit: 'people',
    says: (v) => <>Blocks any campaign whose estimated audience is under <b>{num(v)}</b>.</>,
  },
  min_audience_size_flag: {
    label: 'Audience review threshold', kind: 'number', unit: 'people',
    says: (v) => <>Flags any campaign whose estimated audience is under <b>{num(v)}</b> for review.</>,
  },
  require_exclusion_list: {
    label: 'Require exclusion list', kind: 'bool',
    says: (v) => v
      ? <>Flags any campaign built without an exclusion audience attached.</>
      : <>Campaigns may be built with no exclusion audience.</>,
  },
  objective_whitelist: {
    label: 'Permitted objectives', kind: 'list',
    says: (v) => <>The agent may only build campaigns with these objectives: <b>{v || 'none'}</b>.</>,
  },
  duplicate_detection_days: {
    label: 'Duplicate window', kind: 'number', unit: 'days',
    says: (v) => <>Flags a campaign that closely matches one built in the last <b>{v} days</b>.</>,
  },
  naming_convention: {
    label: 'Required name keyword', kind: 'text',
    says: (v) => <>Every campaign name must contain <b>{v || '—'}</b>.</>,
  },
  require_approved_creative: {
    label: 'Creative must be approved', kind: 'bool',
    says: (v) => v
      ? <>The agent can only use assets a human has approved and published to the platform.</>
      : <>The agent may use any asset in the library, approved or not.</>,
  },
  // Google Search. Values are Google's own names, as the engine compares them.
  max_cpc_bid_pence: {
    label: 'CPC bid ceiling', kind: 'money', unit: 'per click',
    // Pence shown as pounds with pence: a £2.50 cap is not "£3".
    says: (v) => <>Blocks any campaign with a bid above <b>£{(Number(v || 0) / 100).toFixed(2)}</b> a click, or a bidding strategy that lets Google set bids past it.</>,
  },
  match_type_allowlist: {
    label: 'Permitted match types', kind: 'list',
    says: (v) => <>Keywords may only use these match types: <b>{v || 'none'}</b>.</>,
  },
  require_negative_keywords: {
    label: 'Require negative keywords', kind: 'bool',
    says: (v) => v
      ? <>Every Search campaign must carry at least one negative keyword.</>
      : <>Search campaigns may be built with no negative keywords.</>,
  },
  bidding_strategy_allowlist: {
    label: 'Permitted bidding strategies', kind: 'list',
    says: (v) => <>The agent may only use these bidding strategies: <b>{v || 'none'}</b>.</>,
  },
  final_url_domain_allowlist: {
    label: 'Permitted landing domains', kind: 'list',
    says: (v) => <>Ads may only send people to these domains or their subdomains: <b>{v || 'none'}</b>.</>,
  },
  creative_must_match_objective: {
    label: 'Creative matches objective', kind: 'bool',
    says: (v) => v
      ? <>Flags a build that uses a creative tagged for a different objective.</>
      : <>Any approved creative may be used with any objective.</>,
  },
};

const metaFor = (name) => META[name] || {
  label: name.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()),
  kind: 'text',
  says: (v) => <>Current value: <b>{String(v)}</b>.</>,
};

const isTruthy = (v) => v === true || v === 'true' || v === '1' || v === 1;

export default function RulesConfig() {
  const { accountId, selectedId, selected, isAll, platform } = useClient();

  const [rules, setRules] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState({});
  const [query, setQuery] = useState('');

  const reload = useCallback(() => {
    if (!accountId) { setLoading(false); return; }
    setLoading(true);
    setDirty({});

    apiGet(`/rules/${accountId}`)
      .then((data) => { setRules(data.rules || []); setError(null); setLoading(false); })
      .catch((err) => {
        setError(err instanceof AuthError ? err.message : err.message || 'Could not load rules.');
        setLoading(false);
      });

    // Used only for the impact column. A failure here costs the numbers, not
    // the page.
    const params = new URLSearchParams();
    if (selectedId && selectedId !== 'ALL') params.set('client_id', selectedId);
    apiGet(`/decisions${params.toString() ? `?${params}` : ''}`)
      .then((data) => setDecisions(data.decisions || []))
      .catch(() => setDecisions([]));
  }, [accountId, selectedId]);

  useEffect(() => { reload(); }, [reload]);

  // The last 30 days, on THIS platform. A Meta block on the daily budget and
  // a Google one share a rule_triggered key, so without the platform each
  // tab would count the other's fires as its own.
  const recent = useMemo(() => decisions.filter((d) => {
    const t = new Date(d.timestamp);
    return !isNaN(t) && (Date.now() - t.getTime()) / 36e5 <= 30 * 24
      && (!platform || (d.platform || 'meta') === platform);
  }), [decisions, platform]);

  /**
   * Two different questions, so two different answers.
   *
   *   A threshold can be asked "what would this value catch?", and the answer
   *   moves as you drag the number — which is the whole point of showing it.
   *
   *   A rule with no threshold can only be asked "how often has this fired?",
   *   which is history, not a prediction. Saying "would catch" for both would
   *   be a lie about one of them.
   */
  const firedCount = (rule) => {
    const key = `${rule.rule_category}.${rule.rule_name}`;
    return recent.filter((d) => d.rule_triggered === key).length;
  };

  const impactFor = (rule) => {
    const meta = metaFor(rule.rule_name);
    const value = Number(rule.rule_value);

    if (meta.kind === 'money' && Number.isFinite(value)) {
      const pounds = value / 100;
      return { n: recent.filter((d) => (d.spend_value || 0) > pounds).length, verb: 'would catch' };
    }
    if (rule.rule_name === 'min_audience_size' || rule.rule_name === 'min_audience_size_flag') {
      // /decisions does not carry audience size, so this one can only be
      // answered from history like the rest.
      return { n: firedCount(rule), verb: 'fired' };
    }
    return { n: firedCount(rule), verb: 'fired' };
  };

  const patchLocal = (name, changes) => {
    setRules((list) => list.map((r) => (r.rule_name === name ? { ...r, ...changes } : r)));
    setDirty((d) => ({ ...d, [name]: true }));
  };

  const save = async () => {
    const names = Object.keys(dirty);
    if (!names.length) return;
    setSaving(true);
    setSaveError(null);
    try {
      // apiPatch throws on any non-2xx, so a rejected write can no longer be
      // reported as a successful save.
      await Promise.all(names.map((name) => {
        const rule = rules.find((r) => r.rule_name === name);
        return apiPatch(`/rules/${accountId}`, {
          rule_name: rule.rule_name,
          rule_category: rule.rule_category,
          rule_value: String(rule.rule_value),
          is_active: rule.is_active,
        });
      }));
      setDirty({});
    } catch (err) {
      setSaveError(err instanceof AuthError
        ? err.message
        : 'Changes were not saved. Nothing has been written — try again.');
    }
    setSaving(false);
  };

  if (isAll) {
    return (
      <>
        <div className="page-header">
          <h1 className="page-title">Rules Config</h1>
          <p className="page-subtitle">Governance rules are configured per client</p>
        </div>
        <div className="card">
          <div className="empty">
            <h3>Select a client</h3>
            <p>Choose a client from the switcher above to view and edit its rules.</p>
          </div>
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <div className="page-header">
          <h1 className="page-title">Rules Config</h1>
          <p className="page-subtitle">Loading the rule set…</p>
        </div>
        <div className="skeleton" style={{ height: 420, borderRadius: 'var(--radius-md)' }} />
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="page-header"><h1 className="page-title">Rules Config</h1></div>
        <div className="banner bad">
          <Icon name="alert" size={16} strokeWidth={2.2} />
          <span>{error}</span>
        </div>
      </>
    );
  }

  const dirtyNames = Object.keys(dirty);
  const shown = rules.filter((r) =>
    !query || `${r.rule_name} ${metaFor(r.rule_name).label}`.toLowerCase().includes(query.toLowerCase()));
  const activeCount = rules.filter((r) => r.is_active).length;

  return (
    <>
      <div className="page-header split">
        <div>
          <h1 className="page-title">Rules Config</h1>
          <p className="page-subtitle">
            What {selected ? `${selected.name}'s` : 'this client’s'} agent is allowed to do
            {platform ? <> on <b>{PLATFORM_LABEL[platform] || platform}</b></> : null}.
            Every change is versioned — nothing is overwritten.
          </p>
        </div>
        <div className="hdr-actions">
          <PlatformSwitch lockedReason={dirtyNames.length ? 'Save or discard your changes before switching account' : null} />
          <label className="search">
            <Icon name="search" size={14} strokeWidth={2.2} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find a rule"
              aria-label="Find a rule"
            />
          </label>
        </div>
      </div>

      {dirtyNames.length > 0 && (
        <div className="savebar">
          <Icon name="alert" size={16} strokeWidth={2.2} />
          <b>{dirtyNames.length} unsaved change{dirtyNames.length > 1 ? 's' : ''}</b>
          <span className="list">
            {dirtyNames.map((n) => metaFor(n).label).join(' · ')}
          </span>
          <span className="grow" />
          <button className="btn btn-ghost" onClick={reload} disabled={saving}>Discard</button>
          <button className="btn btn-brass" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save and version'}
          </button>
        </div>
      )}

      {saveError && (
        <div className="banner bad">
          <Icon name="alert" size={16} strokeWidth={2.2} />
          <span>{saveError}</span>
        </div>
      )}

      <div className="banner info">
        <Icon name="info" size={16} strokeWidth={2.2} />
        <span>
          <b>{activeCount} of {rules.length} rules active.</b> The right-hand column is measured
          over the last 30 days — thresholds show what the current value would catch, other rules
          show how often they have fired.
        </span>
      </div>

      {rules.length === 0 ? (
        <div className="card">
          <div className="empty">
            <h3>No rules configured</h3>
            <p>
              This client has no governance rules, so every action an agent proposes will be
              blocked. Rules must be configured before an agent can operate.
            </p>
          </div>
        </div>
      ) : (
        CATEGORIES.map(([key, label]) => {
          const group = shown.filter((r) => r.rule_category === key);
          if (!group.length) return null;
          return (
            <div className="card" key={key} style={{ marginBottom: 'var(--space-5)' }}>
              <div className="card-header">
                <span className="card-title">{label}</span>
                <span className="card-note">
                  {group.filter((r) => r.is_active).length} of {group.length} active
                </span>
              </div>
              <div className="rules-wrap">
                <table className="rules-table">
                  <colgroup>
                    <col style={{ width: '44%' }} />
                    <col style={{ width: '28%' }} />
                    <col style={{ width: '11%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '8%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Rule</th>
                      <th>Setting</th>
                      <th style={{ textAlign: 'right' }}>Last 30 days</th>
                      <th>Outcome</th>
                      <th style={{ textAlign: 'center' }}>Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.map((rule) => (
                      <RuleRow
                        key={rule.rule_name}
                        rule={rule}
                        changed={!!dirty[rule.rule_name]}
                        impact={impactFor(rule)}
                        onChange={patchLocal}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })
      )}
    </>
  );
}

function RuleRow({ rule, changed, impact, onChange }) {
  const meta = metaFor(rule.rule_name);
  const value = rule.rule_value;

  let editor = null;
  if (meta.kind === 'money') {
    editor = (
      <>
        <span className="moneyin">
          <span>£</span>
          <input
            type="number" min="0" step="10"
            value={Math.round(Number(value) / 100)}
            aria-label={`${meta.label} value in pounds`}
            onChange={(e) => onChange(rule.rule_name, { rule_value: String((Number(e.target.value) || 0) * 100) })}
          />
        </span>
        <span className="r-unit">{meta.unit}</span>
      </>
    );
  } else if (meta.kind === 'number') {
    editor = (
      <>
        <span className="moneyin">
          <input
            type="number" min="0"
            value={Number(value) || 0}
            aria-label={`${meta.label} value`}
            onChange={(e) => onChange(rule.rule_name, { rule_value: String(Number(e.target.value) || 0) })}
          />
        </span>
        <span className="r-unit">{meta.unit}</span>
      </>
    );
  } else if (meta.kind === 'bool') {
    editor = (
      <div className="chiplist">
        <button
          aria-pressed={isTruthy(value)}
          onClick={() => onChange(rule.rule_name, { rule_value: isTruthy(value) ? 'false' : 'true' })}
        >
          {isTruthy(value) ? 'Required' : 'Optional'}
        </button>
      </div>
    );
  } else {
    editor = (
      <span className="moneyin" style={{ width: '100%' }}>
        <input
          type="text"
          value={value ?? ''}
          aria-label={`${meta.label} value`}
          style={{ width: '100%' }}
          onChange={(e) => onChange(rule.rule_name, { rule_value: e.target.value })}
        />
      </span>
    );
  }

  const saysValue = meta.kind === 'bool' ? isTruthy(value) : value;

  return (
    <tr className={`rulerow ${rule.is_active ? '' : 'off'} ${changed ? 'changed' : ''}`}>
      <td>
        <span className="r-nm">{meta.label}</span>
        <span className="r-id">{rule.rule_name}</span>
        <div className="r-says">{meta.says(saysValue)}</div>
      </td>
      <td className="r-edit"><div>{editor}</div></td>
      <td style={{ textAlign: 'right' }}>
        <span className={`r-impact ${impact.n > 0 ? 'hot' : ''}`}>
          <b>{impact.n}</b>
          <span className="sub">{impact.verb}</span>
        </span>
      </td>
      <td><span className={`rulec-act ${rule.rule_action}`}>{rule.rule_action}</span></td>
      <td style={{ textAlign: 'center' }}>
        <label className="toggle">
          <input
            type="checkbox"
            checked={!!rule.is_active}
            aria-label={`Turn ${meta.label} on or off`}
            onChange={() => onChange(rule.rule_name, { is_active: !rule.is_active })}
          />
          <span className="toggle-slider" />
        </label>
      </td>
    </tr>
  );
}